import { useCallback, useMemo } from 'react';
import create from 'zustand';
import isEqual from 'lodash/isEqual';
import type { Actor } from '@avatar/types';
import { TtsModelRelease } from '../../../../server/graphql/__generated.types';

export type SelectedPreviewActor = {
  avatarId: number | undefined;
  avatarVariantId: number | undefined;
  version: string | undefined;
};

type PreviewStateAndActions = {
  selectedPreviewActor: SelectedPreviewActor;
  setSelectedPreviewActor: (payload: SelectedPreviewActor) => void;
  clearSelectedPreviewActor: () => void;
};

const initialSelectedPreviewActor = {
  avatarId: undefined,
  avatarVariantId: undefined,
  version: undefined,
};

const useSelectedPreviewActorStore = create<PreviewStateAndActions>(set => ({
  selectedPreviewActor: initialSelectedPreviewActor,
  setSelectedPreviewActor: selectedPreviewActor =>
    set(state => ({
      ...state,
      selectedPreviewActor,
    })),
  clearSelectedPreviewActor: () =>
    set(state => ({
      ...state,
      selectedPreviewActor: initialSelectedPreviewActor,
    })),
}));

const useSelectedPreviewActor = () => {
  const setSelectedPreviewActor = useSelectedPreviewActorStore(
    useCallback(state => state.setSelectedPreviewActor, [])
  );
  const clearSelectedPreviewActor = useSelectedPreviewActorStore(
    useCallback(state => state.clearSelectedPreviewActor, [])
  );
  const selectedPreviewActor = useSelectedPreviewActorStore(
    state => state.selectedPreviewActor
  );
  const setSelectedPreviewActorIfDifferent = useCallback(
    (actor: {
      actor: Actor;
      variant: Actor['variants'][0];
      version: TtsModelRelease['model'] | undefined;
    }) => {
      const formattedActor = {
        avatarId: actor.actor.id,
        avatarVariantId: actor.variant.id,
        version: actor.version || actor.variant.version,
      };
      if (isEqual(formattedActor, selectedPreviewActor)) {
        return;
      }
      setSelectedPreviewActor(formattedActor);
    },
    [setSelectedPreviewActor, selectedPreviewActor]
  );
  const compositeId = useMemo(() => {
    return `${selectedPreviewActor.avatarId}:${selectedPreviewActor.avatarVariantId}:${selectedPreviewActor.version}`;
  }, [selectedPreviewActor]);
  return useMemo(
    () => ({
      compositeId,
      selectedPreviewActor,
      setSelectedPreviewActor,
      clearSelectedPreviewActor,
      setSelectedPreviewActorIfDifferent,
    }),
    [
      compositeId,
      selectedPreviewActor,
      setSelectedPreviewActor,
      clearSelectedPreviewActor,
      setSelectedPreviewActorIfDifferent,
    ]
  );
};

export default useSelectedPreviewActor;
